import React from 'react'

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Fade,
  IconButton
} from '@mui/material'

import WeeklyReportUserTable from './WeeklyReportUserTable'

import { Iconify } from 'src/components/base'
import { WEEKLY_REPORTS_SUB_TABLE_HEAD } from 'src/constants/table.constants'

interface WeeklyReportsSubTableProps {
  timeEntries?: EntriesInfo[]
  open: boolean
}

const nestLevel1 = {
  mx: 2,
  mb: 1.5,
  borderRadius: 2,
  overflow: 'hidden',
  border: (theme: { palette: { divider: string } }) =>
    `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme: {
    palette: {
      mode: string
      grey: { 500_8: string }
      background: { paper: string }
    }
  }) =>
    theme.palette.mode === 'dark'
      ? theme.palette.grey[500_8]
      : theme.palette.background.paper
}

const WeeklyReportsSubTable: React.FC<WeeklyReportsSubTableProps> = ({
  timeEntries,
  open
}) => {
  const [openEntries, setOpenEntries] = React.useState<{
    [key: string]: boolean
  }>({})

  return (
    <>
      {open && (
        <Fade in={open}>
          <Box sx={nestLevel1}>
            {!!timeEntries?.length && (
              <Table size="small" aria-label="collaborators">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? theme.palette.grey[700]
                          : theme.palette.grey[200]
                    }}
                  >
                    {WEEKLY_REPORTS_SUB_TABLE_HEAD.map((head) => (
                      <TableCell
                        key={head.id}
                        align={head.align}
                        width={head.width}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.8125rem',
                          py: 1.25,
                          borderBottom: (theme) =>
                            `1px solid ${theme.palette.divider}`
                        }}
                      >
                        {head.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {timeEntries?.map((entry, index) => (
                    <React.Fragment key={index}>
                      <TableRow
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.action.hover
                          }
                        }}
                        onClick={() => {
                          setOpenEntries(
                            (prev: { [key: string]: boolean }) => ({
                              ...prev,
                              [entry?.name]: !prev[entry.name]
                            })
                          )
                        }}
                      >
                        <TableCell
                          align="center"
                          sx={{
                            p: 0,
                            width: 50,
                            '&.MuiTableCell-root:first-of-type': {
                              paddingLeft: 0
                            }
                          }}
                        >
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            sx={{ p: 0.5 }}
                          >
                            {openEntries[entry.name] ? (
                              <Iconify icon="akar-icons:arrow-up" width={18} />
                            ) : (
                              <Iconify
                                icon="akar-icons:arrow-down"
                                width={18}
                              />
                            )}
                          </IconButton>
                        </TableCell>

                        <TableCell
                          sx={{
                            py: 1.25,
                            fontWeight: 500
                          }}
                        >
                          {entry?.name}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1.25 }}>
                          {entry?.total}
                        </TableCell>
                      </TableRow>

                      {openEntries[entry.name] && !!entry?.entries.length && (
                        <TableRow sx={{ '& > *': { py: 0 } }}>
                          <TableCell
                            colSpan={3}
                            sx={{ p: 0, verticalAlign: 'top' }}
                          >
                            <WeeklyReportUserTable
                              openEntries={openEntries[entry.name]}
                              entriesInfo={entry?.entries}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Fade>
      )}
    </>
  )
}

export default WeeklyReportsSubTable
